import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { CheckCircle2, Award, ArrowRight } from 'lucide-react';

/**
 * Reusable detail dialog used by Modules, Capabilities and Personas sections
 * on the public LandingPage. Renders whatever blocks are passed in:
 *   - `intro` (required)         — long-form paragraph
 *   - `features`                  — bulleted list with check-icons
 *   - `callout`                   — coloured callout card (label + text)
 *   - `benefits`                  — 2-column grid of small benefit cards
 *
 * Visual variant is controlled by `iconBg` ('gradient' | 'soft') so each
 * section keeps its own colour identity (gradient for modules, soft for
 * capabilities/personas).
 */
const DetailDialog = ({
  open,
  onOpenChange,
  icon: Icon,
  title,
  tagline,
  intro,
  iconBg = 'soft',                 // 'gradient' | 'soft'
  features,
  featuresLabel,
  callout,                         // { label, text }
  benefits,
  benefitsLabel,
  ctaLabel,
  ctaHref = '/login',
  closeLabel,
  testId = 'detail-dialog',
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const iconWrapClass =
    iconBg === 'gradient'
      ? 'bg-gradient-to-br from-emerald-500 to-teal-600 shadow-md'
      : 'bg-emerald-100';
  const iconColorClass = iconBg === 'gradient' ? 'text-white' : 'text-emerald-700';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={testId}>
        <DialogHeader>
          <div className="flex items-start gap-4 mb-2">
            <div className={`shrink-0 w-14 h-14 rounded-xl flex items-center justify-center ${iconWrapClass}`}>
              {Icon && <Icon className={`w-7 h-7 ${iconColorClass}`} />}
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-2xl font-bold text-slate-900 leading-tight">
                {title}
              </DialogTitle>
              {tagline && (
                <DialogDescription className="mt-1 text-slate-600">
                  {tagline}
                </DialogDescription>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {intro && (
            <p className="text-slate-700 leading-relaxed">{intro}</p>
          )}

          {Array.isArray(features) && features.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-emerald-700 uppercase tracking-wider mb-3">
                {featuresLabel || t('landing.modules.detail.features_label')}
              </h4>
              <ul className="space-y-2">
                {features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {callout && callout.text && (
            <div className="rounded-lg bg-emerald-50/60 border border-emerald-100 p-4">
              <h4 className="text-sm font-semibold text-emerald-800 mb-2">
                {callout.label}
              </h4>
              <p className="text-sm text-slate-700 leading-relaxed">
                {callout.text}
              </p>
            </div>
          )}

          {Array.isArray(benefits) && benefits.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-emerald-700 uppercase tracking-wider mb-3">
                {benefitsLabel || t('landing.modules.detail.benefits_label')}
              </h4>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700 p-2 rounded bg-slate-50">
                    <Award className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {closeLabel || t('landing.modules.detail.close')}
            </Button>
            <Button
              onClick={() => { onOpenChange(false); navigate(ctaHref); }}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
            >
              {ctaLabel || t('landing.cta.get_started')} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DetailDialog;
